<template>
	<app-content class="h-panel">
		<div class="h-panel-bar">
			<span class="h-panel-title">科目余额表</span>
		</div>
		<div class="margin-right-left margin-top">
			<div class="book-toolbar">
				<div class="book-toolbar-main">
					<div class="book-toolbar-period">
						<account-date-choose v-model="periodRange" :range="true"/>
					</div>
					<div class="book-toolbar-actions">
						<Button type="primary" @click="exportData">导出</Button>
						<Checkbox v-model="hideZeroBalance" class="book-inline-checkbox">隐藏零余额科目</Checkbox>
						<Checkbox v-model="showYTD" class="book-inline-checkbox">显示本年累计</Checkbox>
						<Checkbox v-model="showNumPrice" v-if="amountMode === 'base'" class="book-inline-checkbox">显示数量金额</Checkbox>
					</div>
				</div>
				<div class="book-toolbar-filters">
					<div class="book-filter-group">
						<label class="book-filter-label">金额方式</label>
						<Select
							v-model="amountMode"
							:datas="amountModeOptions"
							keyName="key"
							titleName="title"
							:deletable="false"
							class="book-filter-select mode-select"
						/>
					</div>
					<div class="book-filter-group" v-if="amountMode === 'foreign'">
						<label class="book-filter-label">币种</label>
						<Select
							v-model="currencyCode"
							:datas="currencyOptions"
							keyName="key"
							titleName="title"
							filterable
							:deletable="false"
							class="book-filter-select currency-select"
						/>
					</div>
				</div>
			</div>
		</div>
		<div class="h-panel-body">
			<div class="table-scroll-wrapper">
				<Loading text="科目余额数据加载中..." :loading="loading"></Loading>
				<!-- ==================== 本币金额方式 ==================== -->
				<template v-if="amountMode === 'base'">
					<table class="balance" v-if="!showNumPrice">
					<thead class="header">
					<tr>
						<td rowspan="2">科目编码</td>
						<td rowspan="2">科目名称</td>
						<td colspan="2">期初余额</td>
						<td colspan="2">本期发生额</td>
						<td colspan="2" v-if="showYTD">本年累计发生额</td>
						<td colspan="2">期末余额</td>
					</tr>
					<tr>
						<td>借方</td>
						<td>贷方</td>
						<template v-if="showYTD">
							<td>借方</td>
							<td>贷方</td>
						</template>
						<td>借方</td>
						<td>贷方</td>
						<td>借方</td>
						<td>贷方</td>
					</tr>
					</thead>
					<tbody>
					<tr v-for="item in displayDataList" :key="item.subjectId" :class="{'total-row': isTotalRow(item) || isSubtotalRow(item), 'subtotal-row': isSubtotalRow(item), 'unbalanced-row': isTotalRow(item) && !isBalanced(item)}">
						<td :style="{'padding-left':(item.level)*10+'px', 'text-align':'left'}">{{item.code}}</td>
						<td :style="{'padding-left':(item.level)*10+'px', 'text-align':'left'}">
							<span class="text-hover" :class="{'total-text': isTotalRow(item) || isSubtotalRow(item)}">
								<i v-if="isSubtotalRow(item)" class="icon-layers subtotal-icon"></i>
								{{formatTotalRowName(item)}}
							</span>
							<span v-if="isTotalRow(item) && !isBalanced(item)" class="balance-warning">
								⚠️ 不平衡
							</span>
						</td>
						<td align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{getDirectionBalance(item, 'beginning', '借')|numFormat}}</td>
						<td align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{getDirectionBalance(item, 'beginning', '贷')|numFormat}}</td>
						<td align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{item.currentDebitAmount|numFormat}}</td>
						<td align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{item.currentCreditAmount|numFormat}}</td>
						<template v-if="showYTD">
							<td align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{item.yearToDateDebitAmount|numFormat}}</td>
							<td align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{item.yearToDateCreditAmount|numFormat}}</td>
						</template>
						<td align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{getDirectionBalance(item, 'ending', '借')|numFormat}}</td>
						<td align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{getDirectionBalance(item, 'ending', '贷')|numFormat}}</td>
					</tr>
					<tr v-if="!displayDataList.length">
						<td :colspan="showYTD ? 10 : 8" class="text-center padding">暂无数据</td>
					</tr>
					</tbody>
					</table>
					<table class="balance" v-else>
					<thead class="header">
					<tr>
						<td rowspan="2">科目编码</td>
						<td rowspan="2">科目名称</td>
						<td colspan="4">期初余额</td>
						<td colspan="4">本期发生额</td>
						<td colspan="4" v-if="showYTD">本年累计发生额</td>
						<td colspan="4">期末余额</td>
					</tr>
					<tr>
						<td>借方数量</td>
						<td>借方金额</td>
						<td>贷方数量</td>
						<td>贷方金额</td>
						<td>借方数量</td>
						<td>借方金额</td>
						<td>贷方数量</td>
						<td>贷方金额</td>
						<template v-if="showYTD">
							<td>借方数量</td>
							<td>借方金额</td>
							<td>贷方数量</td>
							<td>贷方金额</td>
						</template>
						<td>借方数量</td>
						<td>借方金额</td>
						<td>贷方数量</td>
						<td>贷方金额</td>
					</tr>
					</thead>
					<tbody>
					<tr v-for="(item, index) in displayDataList" :key="item.isSubtotal ? 'subtotal-' + index : item.subjectId" :class="{'total-row': isTotalRow(item) || isSubtotalRow(item), 'subtotal-row': isSubtotalRow(item), 'unbalanced-row': isTotalRow(item) && !isBalanced(item)}">
						<td :style="{'padding-left':(item.level)*10+'px', 'text-align':'left'}">{{item.code}}</td>
						<td :style="{'padding-left':(item.level)*10+'px', 'text-align':'left'}">
							<span class="text-hover" :class="{'total-text': isTotalRow(item) || isSubtotalRow(item)}">
								<i v-if="isSubtotalRow(item)" class="icon-layers subtotal-icon"></i>
								{{formatTotalRowName(item)}}
							</span>
						</td>
						<td class="text-right">&nbsp;</td>
						<td class="text-right" align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{item.beginningDebitBalance|numFormat}}</td>
						<td class="text-right">&nbsp;</td>
						<td class="text-right" align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{item.beginningCreditBalance|numFormat}}</td>
						<td class="text-right" align="right">{{item.currentDebitAmountNum|numFormat}}</td>
						<td class="text-right" align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{item.currentDebitAmount|numFormat}}</td>
						<td class="text-right" align="right">{{item.currentCreditAmountNum|numFormat}}</td>
						<td class="text-right" align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{item.currentCreditAmount|numFormat}}</td>
						<template v-if="showYTD">
							<td class="text-right" align="right">{{item.yearToDateDebitAmountNum|numFormat}}</td>
							<td class="text-right" align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{item.yearToDateDebitAmount|numFormat}}</td>
							<td class="text-right" align="right">{{item.yearToDateCreditAmountNum|numFormat}}</td>
							<td class="text-right" align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{item.yearToDateCreditAmount|numFormat}}</td>
						</template>
						<td class="text-right" align="right">{{item.endingDebitBalanceNum|numFormat}}</td>
						<td class="text-right" align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{item.endingDebitBalance|numFormat}}</td>
						<td class="text-right" align="right">{{item.endingCreditBalanceNum|numFormat}}</td>
						<td class="text-right" align="right" :class="{'total-amount': isTotalRow(item) || isSubtotalRow(item)}">{{item.endingCreditBalance|numFormat}}</td>
					</tr>
					<tr v-if="!displayDataList.length">
						<td :colspan="showYTD ? 18 : 14" class="text-center padding">暂无数据</td>
					</tr>
					</tbody>
					</table>
				</template>

				<!-- ==================== 外币金额方式 ==================== -->
				<!--
					规则：
					- isCurrencyLeaf=false：科目汇总行，本币字段有效，原币字段留空（不跨币种汇总）
					- isCurrencyLeaf=true：币种叶节点，原币 + 本币均显示
				-->
				<template v-else>
					<table class="balance foreign-table">
					<thead class="header">
					<tr>
						<td rowspan="2">科目编码</td>
						<td rowspan="2">科目名称</td>
						<td rowspan="2">币种</td>
						<td colspan="4">期初余额</td>
						<td colspan="4">本期发生额</td>
						<td colspan="4" v-if="showYTD">本年累计发生额</td>
						<td colspan="4">期末余额</td>
					</tr>
					<tr>
						<!-- 期初 -->
						<td>原币借方</td><td>原币贷方</td><td>本币借方</td><td>本币贷方</td>
						<!-- 本期 -->
						<td>原币借方</td><td>原币贷方</td><td>本币借方</td><td>本币贷方</td>
						<!-- 本年 -->
						<template v-if="showYTD">
							<td>原币借方</td><td>原币贷方</td><td>本币借方</td><td>本币贷方</td>
						</template>
						<!-- 期末 -->
						<td>原币借方</td><td>原币贷方</td><td>本币借方</td><td>本币贷方</td>
					</tr>
					</thead>
					<tbody>
					<tr v-for="(item, idx) in foreignDisplayList"
					    :key="'f-' + idx"
					    :class="{
					      'total-row': isTotalRow(item) || isSubtotalRow(item),
					      'subtotal-row': isSubtotalRow(item),
					      'currency-leaf-row': item.isCurrencyLeaf
					    }">
						<!-- 科目编码 -->
						<td :style="{'padding-left': (item.level || 0) * 10 + 'px', 'text-align':'left'}">
							{{item.code}}
						</td>
						<!-- 科目名称 -->
						<td :style="{'padding-left': (item.level || 0) * 10 + 'px', 'text-align':'left'}">
							<span :class="{'total-text': isTotalRow(item) || isSubtotalRow(item)}">
								<i v-if="isSubtotalRow(item)" class="icon-layers subtotal-icon"></i>
								{{item.name || item.currencyName}}
							</span>
						</td>
						<!-- 币种列：仅币种叶节点显示 -->
						<td class="text-center currency-tag">
							<span v-if="item.isCurrencyLeaf" class="currency-badge">{{item.currencyCode}}</span>
						</td>
						<!-- 期初余额：原币（叶节点才显示；父级汇总行留空，不跨币种相加） -->
						<td class="text-right">{{item.isCurrencyLeaf ? formatOrigin(item.openingDebitOrigin) : ''}}</td>
						<td class="text-right">{{item.isCurrencyLeaf ? formatOrigin(item.openingCreditOrigin) : ''}}</td>
						<!-- 期初余额：本币 -->
						<td class="text-right" :class="{'total-amount': isTotalRow(item)}">
							{{item.beginningDebitBalance|numFormat}}
						</td>
						<td class="text-right" :class="{'total-amount': isTotalRow(item)}">
							{{item.beginningCreditBalance|numFormat}}
						</td>
						<!-- 本期发生额：原币 -->
						<td class="text-right">{{item.isCurrencyLeaf ? formatOrigin(item.periodDebitOrigin) : ''}}</td>
						<td class="text-right">{{item.isCurrencyLeaf ? formatOrigin(item.periodCreditOrigin) : ''}}</td>
						<!-- 本期发生额：本币 -->
						<td class="text-right" :class="{'total-amount': isTotalRow(item)}">{{item.currentDebitAmount|numFormat}}</td>
						<td class="text-right" :class="{'total-amount': isTotalRow(item)}">{{item.currentCreditAmount|numFormat}}</td>
						<!-- 本年累计：原币 -->
						<template v-if="showYTD">
							<td class="text-right">{{item.isCurrencyLeaf ? formatOrigin(item.ytdDebitOrigin) : ''}}</td>
							<td class="text-right">{{item.isCurrencyLeaf ? formatOrigin(item.ytdCreditOrigin) : ''}}</td>
							<!-- 本年累计：本币 -->
							<td class="text-right" :class="{'total-amount': isTotalRow(item)}">{{item.yearToDateDebitAmount|numFormat}}</td>
							<td class="text-right" :class="{'total-amount': isTotalRow(item)}">{{item.yearToDateCreditAmount|numFormat}}</td>
						</template>
						<!-- 期末余额：原币 -->
						<td class="text-right">{{item.isCurrencyLeaf ? formatOrigin(item.closingDebitOrigin) : ''}}</td>
						<td class="text-right">{{item.isCurrencyLeaf ? formatOrigin(item.closingCreditOrigin) : ''}}</td>
						<!-- 期末余额：本币 -->
						<td class="text-right" :class="{'total-amount': isTotalRow(item)}">{{item.endingDebitBalance|numFormat}}</td>
						<td class="text-right" :class="{'total-amount': isTotalRow(item)}">{{item.endingCreditBalance|numFormat}}</td>
					</tr>
					<tr v-if="!foreignDisplayList.length">
						<td :colspan="showYTD ? 19 : 15" class="text-center padding">暂无数据</td>
					</tr>
					</tbody>
					</table>
				</template>
			</div>
		</div>
	</app-content>
</template>

<script>
	import accountBookPeriodRange from '@/mixins/accountBookPeriodRange'

	export default {
		name: "SubjectBalance",
		mixins: [accountBookPeriodRange],
		data() {
			return {
				dataList: [],
				filteredDataList: [],
				foreignDataList: [],
				periodRange: {
					startDate: null,
					endDate: null
				},
				showNumPrice: false,
				hideZeroBalance: true,
				showYTD: false,
				loading: false,
				// 外币金额方式参数
				amountMode: 'base',       // 'base' | 'foreign'
				currencyCode: '',          // 空字符串 = 全部币种
				currencyList: []           // 可选币种列表（从账套配置获取）
			}
		},
		watch: {
			periodRange: {
				handler() {
					if (!this.accountBookPeriodRangeReady) return;
					this.loadList();
				},
				deep: true
			},
			showNumPrice() {
				if (this.amountMode === 'base') this.loadList();
			},
			hideZeroBalance() {
				if (this.amountMode === 'base') {
					this.filterDataList();
				}
			},
			showYTD() {
				// 仅影响展示，不需要重查
			},
			amountMode(newVal) {
				if (newVal === 'foreign') {
					this.currencyCode = '';
				} else {
					this.currencyCode = '';
				}
				this.loadList();
			},
			currencyCode() {
				if (this.amountMode === 'foreign') {
					this.loadList();
				}
			}
		},
		computed: {
			amountModeOptions() {
				return [
					{key: 'base', title: '本币金额方式'},
					{key: 'foreign', title: '外币金额方式'}
				];
			},
			currencyOptions() {
				if (this.amountMode === 'foreign') {
					const options = [{key: '', title: '全部币种'}];
					return options.concat((this.currencyList || []).map(item => ({
						key: item.code,
						title: `${item.code} - ${item.name || item.code}${item.localCurrency ? '（本位币）' : ''}`
					})));
				}
				// 本币金额方式：显示全部币种 + 全部币种选项
				const options = [{key: '', title: '全部币种'}];
				return options.concat((this.currencyList || []).map(item => ({
					key: item.code,
					title: `${item.code} - ${item.name || item.code}`
				})));
			},
			displayDataList() {
				return this.hideZeroBalance ? this.filteredDataList : this.dataList;
			},
			/** 外币模式展示列表：支持隐藏零余额 */
			foreignDisplayList() {
				if (!this.hideZeroBalance) return this.foreignDataList;
				return this.foreignDataList.filter(item => {
					// 合计/小计行始终保留
					if (this.isTotalRow(item) || this.isSubtotalRow(item)) return true;
					// 检查是否有非零金额
					const vals = [
						item.openingDebitOrigin, item.openingCreditOrigin,
						item.beginningDebitBalance, item.beginningCreditBalance,
						item.periodDebitOrigin, item.periodCreditOrigin,
						item.currentDebitAmount, item.currentCreditAmount,
						item.closingDebitOrigin, item.closingCreditOrigin,
						item.endingDebitBalance, item.endingCreditBalance
					];
					return vals.some(v => v != null && Math.abs(v) >= 0.01);
				});
			}
		},
		methods: {
			onAccountBookPeriodRangeReady() {
				this.loadCurrencyList();
				this.loadList();
			},
			buildRangeParams() {
				return {
					startDate: this.periodRange.startDate,
					endDate: this.periodRange.endDate
				};
			},
			/** 加载账套内的可用币种列表 */
			loadCurrencyList() {
				if (this.$api.setting && this.$api.setting.currency) {
					this.$api.setting.currency.list().then(({ data }) => {
						this.currencyList = Array.isArray(data) ? data : (data && data.data) || [];
					}).catch(() => {});
				}
			},
			loadList() {
				this.loading = true;
				if (this.amountMode === 'foreign') {
					this.$api.accountbook.loadSubjectBalance({
						...this.buildRangeParams(),
						amountMode: 'foreign',
						currencyCode: this.currencyCode || ''
					}).then(({ data }) => {
						this.foreignDataList = data || [];
						this.loading = false;
					});
				} else {
					this.$api.accountbook.loadSubjectBalance({
						...this.buildRangeParams(),
						showNumPrice: this.showNumPrice
					}).then(({ data }) => {
						this.dataList = this.calculateCategorySubtotals(data);
						this.filterDataList();
						this.loading = false;
					});
				}
			},
			/** 原币金额格式化：null 显示为空字符串，0 显示为空，正数正常显示 */
			formatOrigin(val) {
				if (val == null || Math.abs(val) < 0.001) return '';
				return this.$options.filters.numFormat(val);
			},
			calculateCategorySubtotals(data) {
				if (!data || data.length === 0) return [];

				const result = [];
				let currentType = null;
				let subtotal = this.createEmptySubtotal();

				const dataRows = data.filter(item => !this.isTotalRow(item));

				dataRows.forEach((item, index) => {
					if (currentType === null && item.type) {
						currentType = item.type;
					}
					if (currentType !== null && item.type && currentType !== item.type) {
						result.push(this.formatSubtotalRow(subtotal, currentType));
						subtotal = this.createEmptySubtotal();
						currentType = item.type;
					}
					result.push(item);
					if (item.level === 1) {
						this.aggregateSubtotal(subtotal, item);
					}
					if (index === dataRows.length - 1) {
						result.push(this.formatSubtotalRow(subtotal, currentType));
					}
				});

				const totalRow = data.find(item => this.isTotalRow(item));
				if (totalRow) result.push(totalRow);

				return result;
			},
			createEmptySubtotal() {
				return {
					beginningDebitBalance: 0, beginningCreditBalance: 0,
					currentDebitAmount: 0, currentCreditAmount: 0,
					currentDebitAmountNum: 0, currentCreditAmountNum: 0,
					yearToDateDebitAmount: 0, yearToDateCreditAmount: 0,
					yearToDateDebitAmountNum: 0, yearToDateCreditAmountNum: 0,
					endingDebitBalance: 0, endingCreditBalance: 0,
					endingDebitBalanceNum: 0, endingCreditBalanceNum: 0
				};
			},
			aggregateSubtotal(subtotal, item) {
				const bb = item.beginningBalance || 0;
				if (item.balanceDirection === '借') {
					if (bb >= 0) subtotal.beginningDebitBalance += bb;
					else subtotal.beginningCreditBalance += Math.abs(bb);
				} else {
					if (bb >= 0) subtotal.beginningCreditBalance += bb;
					else subtotal.beginningDebitBalance += Math.abs(bb);
				}
				subtotal.currentDebitAmount += (item.currentDebitAmount || 0);
				subtotal.currentCreditAmount += (item.currentCreditAmount || 0);
				subtotal.currentDebitAmountNum += (item.currentDebitAmountNum || 0);
				subtotal.currentCreditAmountNum += (item.currentCreditAmountNum || 0);
				subtotal.yearToDateDebitAmount += (item.yearToDateDebitAmount || 0);
				subtotal.yearToDateCreditAmount += (item.yearToDateCreditAmount || 0);
				subtotal.yearToDateDebitAmountNum += (item.yearToDateDebitAmountNum || 0);
				subtotal.yearToDateCreditAmountNum += (item.yearToDateCreditAmountNum || 0);
				const eb = item.endingBalance || 0;
				if (item.balanceDirection === '借') {
					if (eb >= 0) subtotal.endingDebitBalance += eb;
					else subtotal.endingCreditBalance += Math.abs(eb);
				} else {
					if (eb >= 0) subtotal.endingCreditBalance += eb;
					else subtotal.endingDebitBalance += Math.abs(eb);
				}
				subtotal.endingDebitBalanceNum += (item.endingDebitBalanceNum || 0);
				subtotal.endingCreditBalanceNum += (item.endingCreditBalanceNum || 0);
			},
			formatSubtotalRow(subtotal, type) {
				return { ...subtotal, name: type + '小计', code: '', level: 0, isSubtotal: true, type: type };
			},
			filterDataList() {
				if (!this.hideZeroBalance) {
					this.filteredDataList = this.dataList;
					return;
				}
				this.filteredDataList = this.dataList.filter(item => {
					if (this.isTotalRow(item) || this.isSubtotalRow(item)) return true;
					const hasBeginningBalance = Math.abs(item.beginningBalance || 0) >= 0.01;
					const hasCurrentAmount = Math.abs(item.currentDebitAmount || 0) >= 0.01 || Math.abs(item.currentCreditAmount || 0) >= 0.01;
					const hasEndingBalance = Math.abs(item.endingBalance || 0) >= 0.01;
					const hasYTDAmount = Math.abs(item.yearToDateDebitAmount || 0) >= 0.01 || Math.abs(item.yearToDateCreditAmount || 0) >= 0.01;
					return hasBeginningBalance || hasCurrentAmount || hasEndingBalance || hasYTDAmount;
				});
			},
			getDirectionBalance(item, period, direction) {
				if (this.isTotalRow(item) || this.isSubtotalRow(item)) {
					if (period === 'beginning') {
						return direction === '借' ? (item.beginningDebitBalance || 0) : (item.beginningCreditBalance || 0);
					} else {
						return direction === '借' ? (item.endingDebitBalance || 0) : (item.endingCreditBalance || 0);
					}
				}
				let balance = period === 'beginning' ? (item.beginningBalance || 0) : (item.endingBalance || 0);
				if (item.balanceDirection === '借') {
					if (balance >= 0) return direction === '借' ? balance : 0;
					else return direction === '贷' ? Math.abs(balance) : 0;
				} else {
					if (balance >= 0) return direction === '贷' ? balance : 0;
					else return direction === '借' ? Math.abs(balance) : 0;
				}
			},
			exportData() {
				let params = {
					...this.buildRangeParams(),
					amountMode: this.amountMode
				};
				if (this.amountMode === 'foreign') {
					params.currencyCode = this.currencyCode || '';
				} else {
					params.showNumPrice = this.showNumPrice;
				}
				window.open(`/api/accountbook/export/subjectBalance?${new URLSearchParams(params).toString()}`);
			},
			isTotalRow(item) {
				return item.name === '合计' || (item.name && item.name.includes('合计'));
			},
			isSubtotalRow(item) {
				return item && item.isSubtotal;
			},
			isBalanced(item) {
				if (!this.isTotalRow(item)) return true;
				const beginningBalanced = Math.abs((item.beginningDebitBalance || 0) - (item.beginningCreditBalance || 0)) < 0.01;
				const endingBalanced = Math.abs((item.endingDebitBalance || 0) - (item.endingCreditBalance || 0)) < 0.01;
				return beginningBalanced && endingBalanced;
			},
			formatTotalRowName(item) {
				if (this.isSubtotalRow(item)) return item.name;
				if (!this.isTotalRow(item)) return item.name;
				let result = '合计';
				const beginningDiff = (item.beginningDebitBalance || 0) - (item.beginningCreditBalance || 0);
				const endingDiff = (item.endingDebitBalance || 0) - (item.endingCreditBalance || 0);
				if (Math.abs(beginningDiff) >= 0.01 || Math.abs(endingDiff) >= 0.01) {
					result += ' [';
					if (Math.abs(beginningDiff) >= 0.01) result += `期初差额：${this.$options.filters.numFormat(beginningDiff)}`;
					if (Math.abs(endingDiff) >= 0.01) {
						if (Math.abs(beginningDiff) >= 0.01) result += '，';
						result += `期末差额：${this.$options.filters.numFormat(endingDiff)}`;
					}
					result += ']';
				}
				return result;
			}
		}
	}
</script>

<style scoped lang="less">
	.book-toolbar {
		padding: 14px 16px;
		background: linear-gradient(180deg, #fbfdff 0%, #f4f8fc 100%);
		border: 1px solid #dbe7f3;
		border-radius: 10px;
		box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
	}

	.book-toolbar-main {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 12px;
	}

	.book-toolbar-period {
		flex: 1 1 320px;
		min-width: 280px;
	}

	.book-toolbar-actions {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 12px;
		justify-content: flex-end;
	}

	.book-toolbar-filters {
		margin-top: 12px;
		padding-top: 12px;
		border-top: 1px dashed #d7e2ee;
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 16px;
	}

	.book-filter-group {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.book-filter-label {
		font-size: 13px;
		color: #516173;
		font-weight: 600;
		white-space: nowrap;
	}

	.book-inline-checkbox {
		white-space: nowrap;
	}

	.book-filter-select {
		min-width: 180px;
	}

	.currency-select {
		min-width: 220px;
	}

	.book-filter-hint {
		color: #999;
		font-size: 12px;
		font-style: italic;
	}

	.table-scroll-wrapper {
		overflow: auto;
	}

	.balance {
		width: 100%;
		border-collapse: separate;
		border-spacing: 0;

		td {
			padding: 0 8px;
			border: 1px solid #e2e2e2;
			font-size: 12px;
			height: 32px;
		}

		tbody tr:nth-child(even) {
			background-color: #f8fbf8;
		}

		tbody tr:hover {
			background-color: #F0F6FF;
		}

		.header {
			td {
				background-color: #F5F5F5;
				text-align: center;
				font-weight: bold;
				height: 35px;
				position: sticky;
				z-index: 10;
			}
			tr:first-child td { top: 0; }
			tr:nth-child(2) td { top: 35px; }
		}

		.total-row {
			background: linear-gradient(to bottom, #ffffff 0%, #f8fbff 100%) !important;
			font-weight: 700;
			border-top: 3px solid #3788ee !important;
			border-bottom: 3px solid #3788ee !important;
			box-shadow: 0 -2px 8px rgba(55,136,238,0.08), 0 2px 8px rgba(55,136,238,0.08);
			&:hover { background: linear-gradient(to bottom, #f0f7ff 0%, #e8f3ff 100%) !important; }
			td { background-color: transparent !important; font-weight: 700; padding: 10px 8px !important; height: 38px !important; }
			td:first-child { border-left: 5px solid #3788ee !important; padding-left: 10px !important; }
		}

		.subtotal-row {
			background: linear-gradient(to bottom, #fafbfc 0%, #f5f7fa 100%) !important;
			border-top: 2px solid #cbd5e1 !important;
			&:hover { background: linear-gradient(to bottom, #f5f7fa 0%, #f0f3f6 100%) !important; }
			td { background-color: transparent !important; font-weight: 600; color: #334155; padding: 9px 8px !important; height: 36px !important; }
			td:first-child { border-left: 4px solid #94a3b8 !important; padding-left: 10px !important; }
		}

		/* 币种叶节点行 — 外币模式专用 */
		.currency-leaf-row {
			background-color: #f0f7ff !important;
			td { color: #1a56db; }
			&:hover { background-color: #dbeafe !important; }
		}

		.total-text { font-weight: 700; color: #1e3a8a; font-size: 13px; }
		.total-amount { font-weight: 700; color: #1e3a8a; font-size: 13px; }

		.currency-tag { padding: 0 4px; }
		.currency-badge {
			display: inline-block;
			padding: 1px 6px;
			background: #dbeafe;
			color: #1d4ed8;
			border-radius: 3px;
			font-size: 11px;
			font-weight: 600;
		}

		.subtotal-icon {
			display: inline-flex; align-items: center; justify-content: center;
			margin-right: 8px; color: #64748b; font-size: 14px; vertical-align: middle;
			width: 20px; height: 20px;
			background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
			border: 1px solid #cbd5e1; border-radius: 4px;
			box-shadow: 0 1px 3px rgba(0,0,0,0.08); position: relative; top: -1px;
		}

		.balance-warning { color: #ff4757; font-size: 11px; margin-left: 8px; font-weight: normal; }
	}

	.foreign-table {
		min-width: 1400px; // 外币模式列多，允许横向滚动
	}

	@media (max-width: 1200px) {
		.book-toolbar-actions {
			justify-content: flex-start;
		}
	}
</style>
